import { Truck, CheckCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
} from "recharts";
import { RDonutChart } from "./Charts";

interface DeliveryTabProps {
  isLoading: boolean;
  deliveryByWeek: any[];
  deliveryDonutData: any[];
  otifRate: number;
  earlyWarnings?: any;
  onDrillDown?: (config: { type: string; title: string; status?: string }) => void;
}

export function DeliveryTab({
  isLoading,
  deliveryByWeek,
  deliveryDonutData,
  otifRate,
  earlyWarnings,
  onDrillDown,
}: DeliveryTabProps) {
  const deliveryAlerts = earlyWarnings?.delivery || {};
  const urgentDeliveriesCount = deliveryAlerts.urgentDeliveriesCount ?? 0;
  const inTransitCount = deliveryAlerts.inTransitCount ?? 0;
  const realOtifRate = deliveryAlerts.otifRate ?? otifRate;

  return (
    <div className="space-y-3">
      {/* Cảnh báo vận hành giao hàng */}
      <div className="flex flex-wrap items-center gap-2 p-2.5 bg-teal-500/10 border border-teal-500/20 rounded-xl text-xs">
        <span className="font-bold text-teal-700 flex items-center gap-1.5 shrink-0">
          <Truck className="h-3.5 w-3.5" />
          Cảnh báo giao hàng:
        </span>
        <div className="flex flex-wrap items-center gap-2 text-foreground">
          {urgentDeliveriesCount > 0 ? (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "urgent_deliveries",
                  title: "Chuyến giao cần xử lý",
                })
              }
              className="bg-card border-teal-300 text-teal-800 font-semibold cursor-pointer hover:bg-teal-100 transition-colors"
            >
              🚨 {urgentDeliveriesCount} Đơn giao cần xử lý gấp
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-card border-emerald-300 text-emerald-700 font-semibold">
              ✓ Không có đơn giao tồn đọng
            </Badge>
          )}

          {inTransitCount > 0 && (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "urgent_deliveries",
                  title: "Chuyến xe đang giao",
                  status: "IN_TRANSIT",
                })
              }
              className="bg-card border-border text-foreground font-semibold cursor-pointer hover:bg-muted transition-colors"
            >
              ⚠️ {inTransitCount} Chuyến xe đang giao
            </Badge>
          )}

          <Badge
            variant="outline"
            className="bg-card border-emerald-200 text-emerald-700 font-semibold"
          >
            {`▲ Tỷ lệ OTIF ${realOtifRate}%`}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Gauge: OTIF Rate */}
        <div className="bg-card rounded-xl shadow-xs border border-border p-4 flex flex-col justify-center items-center lg:col-span-1">
          <h3 className="text-xs font-bold text-foreground uppercase tracking-wide mb-3 text-center">
            Tỷ lệ OTIF
          </h3>
          {isLoading ? (
            <Skeleton className="h-32 w-32 rounded-full" />
          ) : (
            <div className="relative flex justify-center items-center w-32 h-32 rounded-full border-8 border-teal-100">
              <div
                className="absolute inset-0 rounded-full border-8 border-teal-500"
                style={{
                  clipPath: `polygon(0 0, 100% 0, 100% ${otifRate}%, 0 ${otifRate}%)`,
                }}
              ></div>
              <div className="text-center z-10">
                <span className="text-3xl font-black text-teal-600 font-mono">
                  {otifRate}%
                </span>
              </div>
            </div>
          )}
          <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-500/10 px-2.5 py-1 rounded-full border border-teal-500/20">
            <CheckCircle className="w-3.5 h-3.5" />
            SLA ≥ 95% ({otifRate >= 95 ? 'Đạt' : 'Chưa đạt'})
          </div>
        </div>

        {/* Grouped Column: Orders vs Deliveries by Week */}
        <div className="bg-card rounded-xl shadow-xs border border-border p-3.5 lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-teal-500" /> ĐH vs Giao hàng (5 tuần)
            </h3>
          </div>
          {isLoading ? (
            <Skeleton className="h-[200px] w-full" />
          ) : deliveryByWeek.length === 0 ? (
            <p className="text-xs text-muted-foreground font-medium py-8 text-center">
              Chưa có phát sinh
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={200} minWidth={1} minHeight={1}>
              <BarChart data={deliveryByWeek} margin={{ top: 8, right: 8, left: -10, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="orders" name="Đơn hàng" fill="#6366f1" barSize={18} radius={[4, 4, 0, 0]} />
                <Bar dataKey="deliveries" name="Giao hàng" fill="#14b8a6" barSize={18} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Donut: Delivery Status */}
        <div className="bg-card rounded-xl shadow-xs border border-border p-3.5 lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-emerald-500" /> Trạng thái giao hàng
            </h3>
          </div>
          {isLoading ? (
            <Skeleton className="h-[200px] w-full" />
          ) : (
            <RDonutChart data={deliveryDonutData} totalLabel="phiếu giao" height={200} />
          )}
        </div>
      </div>
    </div>
  );
}
